import { withholdingOn } from './withholding';
import { add, allocateByWeights, fromMinor, multiply, parse, percentOf, roundHalfAwayFromZero, subtract, zero, } from './money';
/**
 * Decide the split.
 *
 *   - Overseas, SEZ and deemed exports are ZERO-RATED - the supply is taxable
 *     but at 0%, which is not the same as exempt and must still appear on the
 *     return. (An SEZ supply "with payment of tax" is IGST; Fint models the
 *     common LUT/bond case, zero-rated, and an org needing the other can pick
 *     an explicit IGST rate.)
 *   - Same state on both sides -> CGST + SGST, half the rate each.
 *   - Different states -> IGST at the full rate.
 *
 * The comparison is on state code, NOT on address text: "Tamil Nadu" and
 * "TAMILNADU" are the same state and a string compare on the customer's typed
 * address would charge them IGST by accident.
 */
export function gstSplitKind(ctx) {
    if (ctx.treatment === 'overseas' || ctx.treatment === 'sez' || ctx.treatment === 'sez_developer' || ctx.treatment === 'deemed_export') {
        return 'zero_rated';
    }
    if (ctx.reverseCharge)
        return 'reverse_charge';
    const supplier = ctx.supplierStateCode.trim().toUpperCase();
    const place = ctx.placeOfSupply.trim().toUpperCase();
    if (!supplier || !place) {
        throw new Error('GST is enabled but the supplier state or place of supply is missing. ' +
            'Without both, the CGST/SGST vs IGST split cannot be decided - and ' +
            'guessing picks the wrong tax for half of all customers.');
    }
    return supplier === place ? 'intra_state' : 'inter_state';
}
/**
 * Rewrite a flat rate into its GST components.
 *
 * An 18% rate becomes CGST 9% + SGST 9% within a state, or IGST 18% across
 * states. The total percentage is identical either way - what changes is which
 * government gets it, which is why this must be stored per line and not
 * recomputed later from an address that may since have been corrected.
 */
export function applyGstSplit(rate, ctx) {
    const kind = gstSplitKind(ctx);
    switch (kind) {
        case 'zero_rated':
            return { ...rate, percentage: 0, components: [{ name: `${rate.name} (zero-rated)`, percentage: 0 }] };
        case 'intra_state': {
            const half = rate.percentage / 2;
            return {
                ...rate,
                components: [
                    { name: `CGST ${trimPct(half)}%`, percentage: half },
                    { name: `SGST ${trimPct(half)}%`, percentage: half },
                ],
            };
        }
        case 'inter_state':
        case 'reverse_charge':
            return {
                ...rate,
                components: [{ name: `IGST ${trimPct(rate.percentage)}%`, percentage: rate.percentage }],
            };
    }
}
/** 9 -> "9", 2.5 -> "2.5". Keeps "CGST 9%" from rendering as "CGST 9.00%". */
function trimPct(p) {
    return String(Number(p.toFixed(3)));
}
// ── the calculator ─────────────────────────────────────────────────────────
/** Resolve a discount spec against a base amount. */
function discountOf(base, spec, currency) {
    if (!spec)
        return zero(currency);
    if (spec.type === 'percentage')
        return percentOf(base, spec.value);
    return parse(spec.value, currency);
}
/**
 * Split a line's tax into its named components.
 *
 * The components are APPORTIONED OUT OF the already-computed total rather than
 * each being charged against the taxable base independently. That is what makes
 * CGST + SGST equal the line's tax to the paise for every input, not just for
 * the ones where it happens to work out: charging 9% twice on 1,234.55 gives
 * 111.11 + 111.11 = 222.22 and charging 18% once gives 222.22, but the two
 * agree only by luck, and "usually adds up" is not a property a tax return can
 * have. allocateByWeights guarantees the parts sum to the whole.
 */
function componentsFor(rate, total) {
    const parts = rate.components?.length
        ? rate.components
        : [{ name: rate.name, percentage: rate.percentage }];
    if (parts.length === 1) {
        return [{ name: parts[0].name, percentage: parts[0].percentage, amount: total }];
    }
    const shares = allocateByWeights(total, parts.map((p) => p.percentage));
    return parts.map((p, i) => ({ name: p.name, percentage: p.percentage, amount: shares[i] }));
}
/**
 * Tax on a taxable base.
 *
 * `inclusive` means the rate is already baked into the figure, so the tax is
 * carved out: on ₹118 at 18%, the tax is ₹18 and the net is ₹100 - NOT ₹21.24.
 * Getting this backwards is the classic invoicing bug and it overcharges by the
 * tax on the tax.
 */
function taxOn(taxable, rate, mode, currency) {
    if (!rate || rate.percentage === 0)
        return zero(currency);
    if (mode === 'inclusive') {
        const divisor = 1 + rate.percentage / 100;
        return subtract(taxable, fromMinor(roundHalfAwayFromZero(taxable.minor / divisor), currency));
    }
    return percentOf(taxable, rate.percentage);
}
/**
 * Compute a whole document.
 *
 * Pure, deterministic, and the single source of every figure on an invoice.
 * Call it on the server before persisting, and in the browser for the live
 * preview - the server's answer is the one that is stored.
 */
export function computeDocument(input) {
    const { currency } = input;
    const docMode = input.taxMode ?? 'exclusive';
    // Pass 1: gross, line discount, net. Nothing here depends on other lines.
    const stage1 = input.lines.map((line) => {
        const unit = parse(line.rate, currency);
        const gross = multiply(unit, line.quantity);
        const discount = discountOf(gross, line.discount, currency);
        return { line, gross, discount, net: subtract(gross, discount) };
    });
    const subtotal = stage1.length ? add(...stage1.map((s) => s.net)) : zero(currency);
    // Pass 2: apportion the document discount across lines by net value.
    //
    // It has to reach the lines rather than being subtracted at the end, because
    // tax is charged on the discounted amount. A ₹100 discount on a document
    // holding one 18% line and one 5% line is not deductible at a single blended
    // rate - each line's tax changes by its own rate.
    const documentDiscount = discountOf(subtotal, input.discount, currency);
    const discountShares = stage1.length
        ? allocateByWeights(documentDiscount, stage1.map((s) => Math.max(0, s.net.minor)))
        : [];
    // Pass 3: base, tax, components, and the ex-tax taxable amount.
    const lines = stage1.map((s, i) => {
        const documentDiscountShare = discountShares[i] ?? zero(currency);
        const base = subtract(s.net, documentDiscountShare);
        const mode = s.line.taxMode ?? docMode;
        const rate = s.line.tax && input.gst ? applyGstSplit(s.line.tax, input.gst) : s.line.tax;
        const taxTotal = taxOn(base, rate, mode, currency);
        // Under an inclusive rate the entered figure already contains the tax, so
        // the ex-tax base is what is left after carving it out.
        const taxable = mode === 'inclusive' ? subtract(base, taxTotal) : base;
        const taxComponents = rate ? componentsFor(rate, taxTotal) : [];
        return {
            id: s.line.id,
            gross: s.gross,
            discount: s.discount,
            net: s.net,
            documentDiscountShare,
            taxable,
            taxTotal,
            taxComponents,
            total: add(taxable, taxTotal),
        };
    });
    const shippingEntered = parse(input.shippingCharge, currency);
    const shippingRate = input.shippingTax && input.gst ? applyGstSplit(input.shippingTax, input.gst) : input.shippingTax;
    const shippingTax = taxOn(shippingEntered, shippingRate, docMode, currency);
    const shippingCharge = docMode === 'inclusive' ? subtract(shippingEntered, shippingTax) : shippingEntered;
    const shippingComponents = shippingRate ? componentsFor(shippingRate, shippingTax) : [];
    const lineTax = lines.length ? add(...lines.map((l) => l.taxTotal)) : zero(currency);
    const taxTotal = add(lineTax, shippingTax);
    const adjustment = parse(input.adjustment, currency);
    // Reverse charge: the tax is computed and reported but the SUPPLIER does not
    // collect it, so it is excluded from what the customer owes. Everything else
    // about the document - the breakdown, the return - still needs the figures.
    const collectedTax = input.gst?.reverseCharge ? zero(currency) : taxTotal;
    // SUM(line total) + shipping + shipping tax + adjustment.
    //
    // No document-discount term: it is already inside each line's taxable amount.
    // Subtracting it here as well is the classic "discount applied twice" bug.
    const taxableTotal = lines.length ? add(...lines.map((l) => l.taxable)) : zero(currency);
    const tcsBase = add(taxableTotal, shippingCharge, collectedTax, adjustment);
    /**
     * TCS, on everything owed INCLUDING GST - which is the opposite of TDS.
     *
     * TCS attaches to consideration received rather than to value supplied
     * (Circular 17/2020), so unlike TDS it is charged on the GST-inclusive
     * figure. Using one base for both is the usual way withholding goes wrong,
     * and it is invisible: the numbers look plausible and reconcile against
     * nothing. See `tdsBaseFor` and `tcsBaseFor` in ./withholding.
     *
     * Charged on the total BEFORE TCS: collecting TCS on TCS is circular and no
     * section asks for it.
     */
    const tcs = input.tcs ? withholdingOn(tcsBase, input.tcs.rate) : zero(currency);
    const total = add(tcsBase, tcs);
    return {
        currency,
        lines,
        subtotal,
        taxableTotal,
        documentDiscount,
        shippingCharge,
        shippingTax,
        taxTotal,
        taxBreakdown: rollUp([...lines.flatMap((l) => l.taxComponents), ...shippingComponents], currency),
        adjustment,
        tcs,
        tcsBase,
        total,
    };
}
/**
 * Collapse per-line components into the summary table an invoice prints:
 * one row per (name, percentage), amounts summed, in first-seen order.
 */
function rollUp(components, currency) {
    const byKey = new Map();
    for (const c of components) {
        if (c.percentage === 0 && c.amount.minor === 0)
            continue;
        const key = `${c.name}|${c.percentage}`;
        const prior = byKey.get(key);
        byKey.set(key, prior ? { ...prior, amount: add(prior.amount, c.amount) } : { ...c, amount: add(zero(currency), c.amount) });
    }
    return [...byKey.values()];
}
//# sourceMappingURL=tax.js.map